import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { apiClient } from '../../lib/api';
import { isLocalDev as checkIsLocalDev } from '../../lib/envDetection';
import { AUTH_TIMEOUT_MS, TimeoutError, withTimeout } from '../../lib/withTimeout';
import {
  decodeJwtPayload,
  getStoredAccessToken,
  isTokenExpiredOrExpiring,
  readSupabaseTokensFromLocalStorage,
  setStoredAccessToken,
  stripSupabaseRefreshTokenFromStorage,
  syncSupabaseAccessTokenInStorage,
} from '../../lib/supabaseAuth';
import { queryClient } from '../../lib/queryClientConfig';
import { updateSupabaseUserWithStoredAccessToken } from '../../lib/supabaseAccessTokenClient';
// RBAC Types and helpers (inlined to avoid module resolution issues)
type Role = 'super_admin' | 'admin' | 'accounting' | 'insurance' | 'readonly';
type Portal = 'admin' | 'reports' | 'sales' | 'accounting' | 'insurance';

const ROLE_PORTAL_ACCESS: Record<Role, Portal[]> = {
  super_admin: ['admin', 'reports', 'sales', 'accounting', 'insurance'],
  admin: ['admin', 'reports', 'sales', 'accounting', 'insurance'],
  accounting: ['accounting'],
  insurance: ['insurance'],
  readonly: ['reports'],
};

const checkPortalAccess = (role: Role, portal: Portal | string): boolean => {
  const portals = ROLE_PORTAL_ACCESS[role];
  return portals ? portals.includes(portal as Portal) : false;
};
// Safe localStorage getter that handles Safari private mode and storage-blocked contexts
const safeGetLocalStorage = (key: string): string | null => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

// Debug logging - only in dev or when explicitly enabled
const DEBUG = import.meta.env.DEV || safeGetLocalStorage('DEBUG_AUTH_PROVIDER') === 'true';

const debugLog = (...args: unknown[]) => {
  // eslint-disable-next-line no-console
  if (DEBUG) console.log(...args);
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSuperAdmin: boolean;
  superAdminLoading: boolean;
  isInsurancePartner: boolean;
  insurancePartnerLoading: boolean;
  role: Role | null;
  roleLoading: boolean;
  canAccessPortal: (portal: Portal | string) => boolean;
  isAccountingUser: boolean;
  accountingUserLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ mfaRequired: boolean }>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMagicLink: (email: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  isPasswordRecovery: boolean;
  mfaChallengeRequired: string | null;
  clearMfaChallengeState: () => void;
  verifyMfaChallenge: (code: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper to cache super admin status
const setCachedSuperAdminStatus = (value: boolean) => {
  try {
    localStorage.setItem('PW_SUPER_ADMIN_STATUS', JSON.stringify({ value, timestamp: Date.now() }));
  } catch {
    // Ignore localStorage errors
  }
};

function buildOptimisticAuthState(accessToken: string): { user: User; session: Session } | null {
  // Only handle JWT-shaped tokens; if not JWT, don't attempt to derive user/session.
  if (!accessToken || accessToken.split('.').length !== 3) return null;

  const payload = decodeJwtPayload(accessToken);
  const userId = typeof payload?.sub === 'string' ? payload.sub : null;
  if (!userId) return null;

  const email = typeof payload?.email === 'string' ? payload.email : undefined;
  const exp = typeof payload?.exp === 'number' ? payload.exp : undefined;

  const optimisticUser = { id: userId, email } as unknown as User;
  const optimisticSession = {
    access_token: accessToken,
    user: optimisticUser,
    expires_at: exp,
  } as unknown as Session;

  return { user: optimisticUser, session: optimisticSession };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [superAdminLoading, setSuperAdminLoading] = useState(true);
  const [isInsurancePartner, setIsInsurancePartner] = useState(false);
  const [insurancePartnerLoading, setInsurancePartnerLoading] = useState(true);
  const [role, setRole] = useState<Role | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [mfaChallengeRequired, setMfaChallengeRequired] = useState<string | null>(null);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const userIdRef = useRef<string | undefined>(undefined);

  // Dedup guard: skip fetchUserRole if we already fetched for this userId
  const lastRoleFetchedForRef = useRef<string | null>(null);

  // Stores aal1 session tokens so we can restore the SDK session before mfa.verify()
  // if side effects (403 → tryRefreshSession → storage corruption) corrupt it.
  const mfaSessionRef = useRef<{ access_token: string; refresh_token: string } | null>(null);

  const [isAccountingUser, setIsAccountingUser] = useState(false);
  const [accountingUserLoading, setAccountingUserLoading] = useState(true);

  // Fetch user role from API
  const fetchUserRole = useCallback(async () => {
    if (!user) {
      setRole(null);
      setRoleLoading(false);
      return;
    }

    // Dedup: skip if we already successfully fetched role for this user
    if (lastRoleFetchedForRef.current === user.id && role !== null) {
      debugLog('[Role] Skipping fetch — already fetched for user:', user.id);
      setRoleLoading(false);
      setSuperAdminLoading(false);
      return;
    }

    try {
      const response = await apiClient.get('/api/admin/me');
      if (response.ok) {
        const data = await response.json();
        const userRole = data?.role as Role | null;
        debugLog('[Role] Fetched role:', userRole);
        setRole(userRole);

        const derivedSuperAdmin = userRole === 'super_admin';
        debugLog('[Role] Derived isSuperAdmin from role:', derivedSuperAdmin);
        setIsSuperAdmin(derivedSuperAdmin);
        setCachedSuperAdminStatus(derivedSuperAdmin);
        setSuperAdminLoading(false);

        // Derive insurance partner access from role
        const derivedPartner =
          derivedSuperAdmin || userRole === 'admin' || userRole === 'insurance';
        debugLog('[Role] Derived isInsurancePartner from role:', derivedPartner);
        setIsInsurancePartner(derivedPartner);
        setInsurancePartnerLoading(false);

        // Derive accounting access from role
        const derivedAccounting =
          derivedSuperAdmin || userRole === 'admin' || userRole === 'accounting';
        debugLog('[Role] Derived isAccountingUser from role:', derivedAccounting);
        setIsAccountingUser(derivedAccounting);
        setAccountingUserLoading(false);

        lastRoleFetchedForRef.current = user.id; // Mark as fetched
      } else {
        debugLog('[Role] Failed to fetch role, status:', response.status);
        setRole(null);
        setIsSuperAdmin(false);
        setCachedSuperAdminStatus(false);
        setSuperAdminLoading(false);
        setIsInsurancePartner(false);
        setInsurancePartnerLoading(false);
        setIsAccountingUser(false);
        setAccountingUserLoading(false);
      }
    } catch (error) {
      debugLog('[Role] Error fetching role:', error);
      setRole(null);
      setIsSuperAdmin(false);
      setCachedSuperAdminStatus(false);
      setSuperAdminLoading(false);
      setIsInsurancePartner(false);
      setInsurancePartnerLoading(false);
      setIsAccountingUser(false);
      setAccountingUserLoading(false);
    } finally {
      setRoleLoading(false);
    }
  }, [user, role]);

  // Check role when user changes — only for fully authenticated (aal2) sessions
  useEffect(() => {
    if (user) {
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      if (currentPath === '/auth/set-password' || currentPath === '/setup-mfa') {
        setRoleLoading(false);
        setSuperAdminLoading(false);
        setInsurancePartnerLoading(false);
        setAccountingUserLoading(false);
        return;
      }

      // Gate: only fetch role once session is aal2 (MFA complete)
      const token = getStoredAccessToken();
      const aal = token ? decodeJwtPayload(token)?.aal : undefined;
      if (aal !== 'aal2') {
        debugLog('[Role] Skipping fetch — session is not aal2 (aal=' + aal + ')');
        // Don't leave role gates waiting forever on an aal1 session —
        // ProtectedRoute deadlocks on roleLoading otherwise.
        setRoleLoading(false);
        setSuperAdminLoading(false);
        setInsurancePartnerLoading(false);
        setAccountingUserLoading(false);
        return;
      }

      fetchUserRole();
    } else {
      const hasProxySession = (() => {
        try {
          return !!(localStorage.getItem('afl_user_id') && getStoredAccessToken());
        } catch {
          return false;
        }
      })();
      setRole(null);
      lastRoleFetchedForRef.current = null; // Reset on sign-out
      if (!hasProxySession) {
        setRoleLoading(false);
      }
    }
  }, [user, fetchUserRole]);

  // Helper to check portal access
  const canAccessPortal = useCallback(
    (portal: Portal | string): boolean => {
      if (isSuperAdmin) return true;
      if (!role) return false;
      return checkPortalAccess(role, portal);
    },
    [isSuperAdmin, role],
  );

  useEffect(() => {
    let isMounted = true;

    const bypassEnabled =
      (import.meta as any).env?.VITE_TEST_BYPASS_AUTH === '1' ||
      (() => {
        try {
          return localStorage.getItem('TEST_BYPASS_AUTH') === '1';
        } catch {
          return false;
        }
      })();

    const bypass = checkIsLocalDev() && bypassEnabled;

    if (bypassEnabled && !checkIsLocalDev()) {
      console.error('Auth bypass attempted in non-local environment');
    }

    if (bypass) {
      console.warn('🔓 AUTH BYPASS ACTIVE - LOCAL DEVELOPMENT ONLY');
      setSession(null);
      setUser({ id: 'test-user', email: 'test@example.com' } as any);
      setIsSuperAdmin(true);
      setIsInsurancePartner(false);
      setInsurancePartnerLoading(false);
      setIsAccountingUser(false);
      setAccountingUserLoading(false);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const { accessToken } = readSupabaseTokensFromLocalStorage(
          (import.meta as any).env?.VITE_SUPABASE_URL,
        );

        if (!accessToken) {
          if (!isMounted) return;
          setSession(null);
          setUser(null);
          setLoading(false);
          setIsSuperAdmin(false);
          return;
        }

        const accessTokenExpired = isTokenExpiredOrExpiring(accessToken, 0);

        const optimistic = accessTokenExpired ? null : buildOptimisticAuthState(accessToken);
        if (optimistic && isMounted) {
          setSession(optimistic.session);
          setUser(optimistic.user);
          userIdRef.current = optimistic.user?.id;
          setLoading(false);

          // ── MFA FIX: Only fetch super admin status for aal2 sessions ──
          // During aal1 (MFA pending), this API call returns 403 →
          // tryRefreshSession() → SDK session corruption.
          setSuperAdminLoading(false);
          // ── END MFA FIX ──
        }

        try {
          if (localStorage.getItem('PW_RECOVERY') === '1') {
            setIsPasswordRecovery(true);
            localStorage.removeItem('PW_RECOVERY');
          }
        } catch (e) {
          console.warn('Failed to check PW_RECOVERY:', e);
        }

        if (!isTokenExpiredOrExpiring(accessToken)) {
          return;
        }

        debugLog('[AuthProvider] Token expiring, refreshing session...');
        const refreshed = await apiClient.tryRefreshSession();
        if (!isMounted) return;

        if (refreshed) {
          if (!optimistic) {
            const { accessToken: refreshedAccessToken } = readSupabaseTokensFromLocalStorage(
              (import.meta as any).env?.VITE_SUPABASE_URL,
            );
            const refreshedOptimistic = refreshedAccessToken
              ? buildOptimisticAuthState(refreshedAccessToken)
              : null;
            if (refreshedOptimistic) {
              setSession(refreshedOptimistic.session);
              setUser(refreshedOptimistic.user);
              setLoading(false);

              // ── MFA FIX: Only fetch super admin status for aal2 sessions ──
              setSuperAdminLoading(false);
              // ── END MFA FIX ──
            } else {
              setLoading(false);
            }
          }
        } else if (accessTokenExpired || !optimistic) {
          debugLog('[AuthProvider] background refresh failed');
          setSession(null);
          setUser(null);
          setLoading(false);
          setIsSuperAdmin(false);
        }
      } catch (e) {
        console.error('[AuthProvider] Failed to initialize auth state:', e);
        if (!isMounted) return;
        setSession(null);
        setUser(null);
        setLoading(false);
        setIsSuperAdmin(false);
      }
    })();

    const isProxySessionAlive = async (): Promise<boolean> => {
      try {
        const hasProxySession = localStorage.getItem('afl_user_id');
        const storedToken = getStoredAccessToken();
        if (!hasProxySession || !storedToken) return false;

        const decoded = decodeJwtPayload(storedToken);
        const exp = typeof decoded?.exp === 'number' ? decoded.exp : 0;
        const isExpired = exp > 0 && exp < Date.now() / 1000;

        if (!isExpired) return true;

        const refreshed = await apiClient.tryRefreshSession();
        if (refreshed) {
          const freshToken = getStoredAccessToken();
          if (freshToken) {
            const freshOptimistic = buildOptimisticAuthState(freshToken);
            if (freshOptimistic) {
              setSession(freshOptimistic.session);
              setUser(freshOptimistic.user);
              userIdRef.current = freshOptimistic.user?.id;
            }
          }
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthProvider] onAuthStateChange event:', event, 'session:', !!session);

      if (!session && (event === 'SIGNED_OUT' || event === 'INITIAL_SESSION')) {
        const alive = await isProxySessionAlive();
        if (alive) {
          console.log(
            '[AuthProvider] Ignoring',
            event,
            '— proxy session active (refreshed if needed)',
          );
          return;
        }
      }
      const previousUserId = userIdRef.current;
      const newUserId = session?.user?.id;
      if (newUserId) userIdRef.current = newUserId;

      const sessionAal = session?.access_token
        ? (decodeJwtPayload(session.access_token)?.aal as string | undefined)
        : undefined;
      const isFullyAuthenticated = sessionAal === 'aal2';

      if (event === 'SIGNED_OUT' || (previousUserId && newUserId && previousUserId !== newUserId)) {
        if (event === 'SIGNED_OUT') {
          const alive = await isProxySessionAlive();
          if (alive) {
            console.log(
              '[AuthProvider] Ignoring SIGNED_OUT — proxy session active (refreshed if needed)',
            );
            return;
          }
        }
      }

      if (event === 'INITIAL_SESSION' && !session) {
        const alive = await isProxySessionAlive();
        if (alive) {
          console.log(
            '[AuthProvider] Ignoring INITIAL_SESSION — proxy session active (refreshed if needed)',
          );
          return;
        }
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (
          currentPath === '/auth/set-password' ||
          currentPath === '/setup-mfa' ||
          currentPath === '/auth/reset'
        ) {
          console.log('[AuthProvider] Ignoring INITIAL_SESSION — on', currentPath);
          return;
        }
        console.log(
          '[AuthProvider] CLEARING STATE — event:',
          event,
          'previousUserId:',
          previousUserId,
          'newUserId:',
          newUserId,
        );
        setRole(null);
        setRoleLoading(true);
        setIsSuperAdmin(false);
        setSuperAdminLoading(true);
        setIsInsurancePartner(false);
        setInsurancePartnerLoading(true);
        setIsAccountingUser(false);
        setAccountingUserLoading(true);

        setCachedSuperAdminStatus(false);
        apiClient.clearSessionCache();
        queryClient.clear();

        try {
          localStorage.removeItem('PW_SUPER_ADMIN_STATUS');
          localStorage.removeItem('selected_tenant_slug');
          localStorage.removeItem('afl_user_id');
          localStorage.removeItem('afl_saved_views');
          localStorage.removeItem('afl_selected_view');
          localStorage.removeItem('afl_column_order');
          localStorage.removeItem('afl_column_visibility');
          localStorage.removeItem('afl_column_sizing');
          localStorage.removeItem('afl_column_pinning');
          localStorage.removeItem('afl_density');
          localStorage.removeItem('afl_compact_headers');
          localStorage.removeItem('afl_zebra_rows');
          localStorage.removeItem('afl_provider_source');
          localStorage.removeItem('PW_RECOVERY');
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }
      }
      if (!session) {
        const hasProxyMarker = (() => {
          try {
            return !!localStorage.getItem('afl_user_id');
          } catch {
            return false;
          }
        })();
        if (hasProxyMarker) {
          console.log('[AuthProvider] Ignoring null session update — proxy marker present');
          isProxySessionAlive().then((alive) => {
            if (!alive) {
              console.log('[AuthProvider] Proxy refresh failed, clearing state');
              setSession(null);
              setUser(null);
              setLoading(false);
              setIsSuperAdmin(false);
              setSuperAdminLoading(false);
            }
          });
          return;
        }
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      userIdRef.current = session?.user?.id;

      if (session && isFullyAuthenticated) {
        setMfaChallengeRequired(null);
        setMfaFactorId(null);
      }

      if (session?.user && isFullyAuthenticated) {
        setSuperAdminLoading(false);
      } else if (session?.user && isMounted) {
        setIsSuperAdmin(false);
        setCachedSuperAdminStatus(false);
        setIsInsurancePartner(false);
        setInsurancePartnerLoading(false);
        setIsAccountingUser(false);
        setAccountingUserLoading(false);
      } else if (!session && isMounted) {
        setIsSuperAdmin(false);
        setSuperAdminLoading(false);
      }

      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      if (event === 'SIGNED_IN' && session && isFullyAuthenticated) {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (currentPath !== '/auth/set-password' && currentPath !== '/setup-mfa') {
          void ensureMfaEnrolled();
        }
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ mfaRequired: boolean }> => {
    const normalizedEmail = email.toLowerCase().trim();

    const parseProxyError = async (
      response: Response,
    ): Promise<{ message: string; code?: string; lockoutSeconds: number | null }> => {
      let payload: any = null;
      try {
        payload = await response.clone().json();
      } catch {
        // Non-JSON responses are handled by status fallback below.
      }

      const lockoutSeconds =
        typeof payload?.lockoutRemainingSeconds === 'number' && payload.lockoutRemainingSeconds > 0
          ? payload.lockoutRemainingSeconds
          : null;
      const code = typeof payload?.code === 'string' ? payload.code : undefined;

      const rawMessage = payload?.message;
      const message =
        typeof rawMessage === 'string'
          ? rawMessage
          : Array.isArray(rawMessage)
            ? rawMessage.join(', ')
            : typeof payload?.error === 'string'
              ? payload.error
              : response.status === 429
                ? 'Account temporarily locked due to too many failed login attempts'
                : 'Invalid email or password';

      if (lockoutSeconds) {
        return {
          message: `${message}. Try again in ${lockoutSeconds}s.`,
          code,
          lockoutSeconds,
        };
      }
      return { message, code, lockoutSeconds };
    };

    const startMfaChallengeFromSupabaseSignIn = async (
      passwordForFallback: string,
      forceChallenge = false,
    ) => {
      // ── MFA FIX: Suppress 401/403 retry to prevent SDK session corruption ──
      apiClient.setMfaFlowActive(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: passwordForFallback,
      });
      if (error) {
        apiClient.setMfaFlowActive(false); // ── MFA FIX: clear on error ──
        throw error;
      }

      // Preserve aal1 session tokens so we can restore the SDK session before
      // mfa.verify() if side effects corrupt it.
      if (data.session?.access_token && data.session?.refresh_token) {
        mfaSessionRef.current = {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        };
      }

      const sessionAccessTokenPayload = decodeJwtPayload(data.session?.access_token || '');
      const sessionAal =
        typeof sessionAccessTokenPayload?.aal === 'string'
          ? String(sessionAccessTokenPayload.aal).toLowerCase()
          : null;
      const shouldChallenge =
        forceChallenge || data.session === null || (sessionAal !== null && sessionAal !== 'aal2');

      if (shouldChallenge) {
        const userFactors = data.user?.factors ?? [];
        const verifiedTotpFromUser = userFactors.find((factor) => {
          const type = String(
            (factor as any)?.factor_type ?? (factor as any)?.type ?? '',
          ).toLowerCase();
          const status = String((factor as any)?.status ?? '').toLowerCase();
          return type === 'totp' && status === 'verified';
        });
        let factorId = verifiedTotpFromUser?.id ?? null;
        if (!factorId) {
          const { data: listData, error: listError } = await withTimeout(
            supabase.auth.mfa.listFactors(),
            AUTH_TIMEOUT_MS,
            'mfa.listFactors (signIn)',
          );
          if (listError) {
            apiClient.setMfaFlowActive(false); // ── MFA FIX: clear on error ──
            throw listError;
          }

          const verifiedTotpFromList = (listData?.totp ?? []).find(
            (factor: any) => String(factor?.status ?? '').toLowerCase() === 'verified',
          );
          factorId = verifiedTotpFromList?.id ?? null;
        }

        if (!factorId) {
          const requireMfa = String((import.meta as any).env?.VITE_REQUIRE_MFA ?? 'true');
          if (requireMfa !== 'true') {
            debugLog('[MFA] No TOTP factor but MFA not required, skipping enrollment redirect');
            apiClient.setMfaFlowActive(false); // ── MFA FIX: clear — no MFA needed ──
            return { mfaRequired: false };
          }
          debugLog('[MFA] No verified TOTP factor found, redirecting to /setup-mfa');
          // signInWithPassword() already persisted the aal1 session to
          // sb-<ref>-auth-token via secureStorage; SetupMfa restores it after reload.
          apiClient.markSetupMfaInFlight();
          // NOTE: mfaFlowActive stays true — SetupMfa.tsx mount will keep it active
          window.location.assign('/setup-mfa');
          return { mfaRequired: true };
        }

        const { data: challengeData, error: challengeError } = await withTimeout(
          supabase.auth.mfa.challenge({ factorId }),
          AUTH_TIMEOUT_MS,
          'mfa.challenge (signIn)',
        );

        if (challengeError) {
          apiClient.setMfaFlowActive(false); // ── MFA FIX: clear on error ──
          throw challengeError;
        }
        if (!challengeData?.id) {
          apiClient.setMfaFlowActive(false); // ── MFA FIX: clear on error ──
          throw new Error('Failed to start MFA challenge');
        }
        setMfaFactorId(factorId);
        setMfaChallengeRequired(challengeData.id);
        // NOTE: mfaFlowActive stays true — verifyMfaChallenge() will clear it on success
        return { mfaRequired: true };
      }

      // No MFA needed — clear the flag
      apiClient.setMfaFlowActive(false); // ── MFA FIX ──

      if (data.session?.access_token && data.session?.refresh_token) {
        return { mfaRequired: false };
      }

      throw new Error('Authentication failed - no session returned');
    };

    const response = await apiClient.postWithoutAuth(
      '/api/auth/sign-in',
      {
        email: normalizedEmail,
        password,
      },
      {
        credentials: 'include',
      },
    );

    if (!response.ok) {
      const proxyError = await parseProxyError(response);
      const normalizedProxyMessage = proxyError.message.toLowerCase();

      const isMfaRequired =
        proxyError.code === 'MFA_REQUIRED' ||
        (response.status === 401 &&
          !proxyError.code &&
          normalizedProxyMessage.includes('no session returned') &&
          !normalizedProxyMessage.includes('invalid email or password'));

      if (isMfaRequired) {
        return startMfaChallengeFromSupabaseSignIn(password, true);
      }

      throw new Error(proxyError.message);
    }

    const payload = await response.json();
    if (!payload?.access_token) {
      throw new Error('Authentication failed - invalid session response');
    }

    const accessToken = String(payload.access_token);
    const decodedAccessToken = decodeJwtPayload(accessToken);
    const payloadAal =
      typeof payload?.user?.aal === 'string' ? String(payload.user.aal).toLowerCase() : null;
    const tokenAal =
      typeof decodedAccessToken?.aal === 'string'
        ? String(decodedAccessToken.aal).toLowerCase()
        : null;
    const effectiveAal = payloadAal ?? tokenAal;

    if (effectiveAal !== 'aal2') {
      const requireMfa = String((import.meta as any).env?.VITE_REQUIRE_MFA ?? 'true');
      if (requireMfa !== 'true') {
        setStoredAccessToken(accessToken);
        syncSupabaseAccessTokenInStorage(accessToken, (import.meta as any).env?.VITE_SUPABASE_URL);
        const userId = decodedAccessToken?.sub ?? payload?.user?.id;
        if (userId) localStorage.setItem('afl_user_id', String(userId));
        const optimistic = buildOptimisticAuthState(accessToken);
        if (optimistic) {
          setSession(optimistic.session);
          setUser(optimistic.user);
        }
        await fetchUserRole();
        // Stamp ref so the useEffect[user] dedup guard skips
        const noMfaUserId = decodedAccessToken?.sub ?? payload?.user?.id;
        if (noMfaUserId) lastRoleFetchedForRef.current = String(noMfaUserId);
        setLoading(false);
        stripSupabaseRefreshTokenFromStorage((import.meta as any).env?.VITE_SUPABASE_URL);
        window.dispatchEvent(new Event('proxy-auth-signed-in'));
        return { mfaRequired: false };
      }
      return startMfaChallengeFromSupabaseSignIn(password, true);
    }

    setStoredAccessToken(accessToken);
    syncSupabaseAccessTokenInStorage(accessToken, (import.meta as any).env?.VITE_SUPABASE_URL);
    const userIdForStorage = decodedAccessToken?.sub ?? payload?.user?.id;
    if (userIdForStorage) localStorage.setItem('afl_user_id', String(userIdForStorage));
    const optimistic = buildOptimisticAuthState(accessToken);
    if (optimistic) {
      setSession(optimistic.session);
      setUser(optimistic.user);
    } else if (payload?.user?.id) {
      setUser({
        id: String(payload.user.id),
        email: String(payload.user.email || normalizedEmail),
      } as User);
    }

    try {
      const roleResponse = await apiClient.get('/api/admin/me');
      if (roleResponse.ok) {
        const roleData = await roleResponse.json();
        const userRole = roleData?.role as Role | null;
        debugLog('[Role] Fetched role in signIn:', userRole);
        setRole(userRole);
        const derivedSuperAdmin = userRole === 'super_admin';
        setIsSuperAdmin(derivedSuperAdmin);
        setCachedSuperAdminStatus(derivedSuperAdmin);

        // Derive insurance partner access from role
        const derivedPartner =
          derivedSuperAdmin || userRole === 'admin' || userRole === 'insurance';
        setIsInsurancePartner(derivedPartner);
        setInsurancePartnerLoading(false);

        // Derive accounting access from role
        const derivedAccounting =
          derivedSuperAdmin || userRole === 'admin' || userRole === 'accounting';
        setIsAccountingUser(derivedAccounting);
        setAccountingUserLoading(false);

        // Stamp ref so the useEffect[user] dedup guard skips the redundant fetch
        lastRoleFetchedForRef.current = userIdForStorage ?? null;
      }
    } catch (e) {
      debugLog('[Role] Error fetching role in signIn:', e);
    }
    setRoleLoading(false);
    setSuperAdminLoading(false);

    setLoading(false);

    stripSupabaseRefreshTokenFromStorage((import.meta as any).env?.VITE_SUPABASE_URL);

    apiClient.setMfaFlowActive(false); // ── MFA FIX: clear after successful non-MFA sign-in ──
    window.dispatchEvent(new Event('proxy-auth-signed-in'));
    return { mfaRequired: false };
  };

  const verifyMfaChallenge = async (code: string) => {
    if (!mfaChallengeRequired || !mfaFactorId) {
      throw new Error('No MFA challenge in progress');
    }

    // ── MFA FIX: Ensure flag stays active during verification ──
    apiClient.setMfaFlowActive(true);

    // Defensive: restore SDK session if side effects corrupted it during aal1 window.
    if (mfaSessionRef.current) {
      try {
        await supabase.auth.setSession(mfaSessionRef.current);
      } catch (e) {
        debugLog('[MFA] Failed to restore session before verify:', e);
      }
    }

    const { data: verifyData, error } = await withTimeout(
      supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: mfaChallengeRequired,
        code,
      }),
      15000,
      'mfa.verify (login)',
    );

    if (error) {
      const normalized = String(error.message || '').toLowerCase();

      if (normalized.includes('expired')) {
        try {
          const { data: challengeData, error: challengeError } = await withTimeout(
            supabase.auth.mfa.challenge({ factorId: mfaFactorId }),
            AUTH_TIMEOUT_MS,
            'mfa.challenge (login refresh)',
          );
          if (!challengeError && challengeData?.id) {
            setMfaChallengeRequired(challengeData.id);
          }
        } catch {
          // Ignore secondary errors
        }

        throw new Error('MFA challenge expired. Please try again.');
      }

      // NOTE: Do NOT clear mfaFlowActive on verify failure — user is still in MFA flow
      throw error;
    }

    setMfaChallengeRequired(null);
    setMfaFactorId(null);
    mfaSessionRef.current = null;

    // CRITICAL: Clear stale stored access token BEFORE reading from Supabase localStorage.
    setStoredAccessToken(null);

    const currentTokens = readSupabaseTokensFromLocalStorage(
      (import.meta as any).env?.VITE_SUPABASE_URL,
    );

    const aal2AccessToken = verifyData?.access_token || currentTokens.accessToken;

    if (aal2AccessToken) {
      setStoredAccessToken(aal2AccessToken);
      syncSupabaseAccessTokenInStorage(
        aal2AccessToken,
        (import.meta as any).env?.VITE_SUPABASE_URL,
      );
      const decoded = decodeJwtPayload(aal2AccessToken);
      const userId = decoded?.sub;
      if (userId) {
        localStorage.setItem('afl_user_id', String(userId));
      }
    }

    const refreshTokenForCookie = verifyData?.refresh_token || currentTokens.refreshToken;

    if (!refreshTokenForCookie) {
      console.error(
        '[MFA] No refresh token available after mfa.verify().',
        'verifyData.refresh_token:',
        !!verifyData?.refresh_token,
        'localStorage refreshToken:',
        !!currentTokens.refreshToken,
      );
    }

    const mfaCompleteResponse = await apiClient.post(
      '/api/auth/mfa-complete',
      refreshTokenForCookie
        ? {
            refreshToken: refreshTokenForCookie,
          }
        : undefined,
      {
        credentials: 'include',
      },
    );
    if (!mfaCompleteResponse.ok) {
      let message = 'Failed to finalize MFA login';
      try {
        const payload = await mfaCompleteResponse.json();
        const rawMessage = payload?.message;
        if (typeof rawMessage === 'string') {
          message = rawMessage;
        } else if (Array.isArray(rawMessage) && rawMessage.length > 0) {
          message = rawMessage.join(', ');
        } else if (typeof payload?.error === 'string') {
          message = payload.error;
        }
      } catch {
        // keep fallback message
      }
      throw new Error(message);
    }

    stripSupabaseRefreshTokenFromStorage((import.meta as any).env?.VITE_SUPABASE_URL);

    // ── MFA FIX: MFA flow complete — re-enable 401/403 handlers ──
    apiClient.setMfaFlowActive(false);
  };

  const clearMfaChallengeState = () => {
    setMfaChallengeRequired(null);
    setMfaFactorId(null);
    mfaSessionRef.current = null;
    apiClient.setMfaFlowActive(false); // ── MFA FIX: clear on cancel/navigation ──
  };

  const signUp = async (email: string, password: string) => {
    const allowedDomains = (import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS || '')
      .split(',')
      .map((d: string) => d.trim().toLowerCase())
      .filter(Boolean);

    if (allowedDomains.length > 0) {
      const emailDomain = email.split('@')[1]?.toLowerCase();
      if (!emailDomain || !allowedDomains.includes(emailDomain)) {
        throw new Error(
          `Signups are restricted to authorized email domains: ${allowedDomains.join(', ')}`,
        );
      }
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const siteUrl = (import.meta as any).env?.VITE_SITE_URL || window.location.origin;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signInWithMagicLink = async (email: string) => {
    const siteUrl = (import.meta as any).env?.VITE_SITE_URL || window.location.origin;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const sendPasswordReset = async (email: string) => {
    const redirectTo = `${(import.meta as any).env?.VITE_SITE_URL || window.location.origin}/auth/callback`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await updateSupabaseUserWithStoredAccessToken({
      password: newPassword,
      data: {
        password_updated_at: new Date().toISOString(),
      },
    });
    if (error) throw error;
    setIsPasswordRecovery(false);
  };

  const signOut = async () => {
    try {
      await apiClient.postWithoutAuth('/api/auth/sign-out', undefined, { credentials: 'include' });
    } catch {
      // ignore
    }

    try {
      localStorage.removeItem('afl_user_id');
      setStoredAccessToken(null);
    } catch {
      /* ignore */
    }

    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    setIsSuperAdmin(false);
    setSuperAdminLoading(false);
    setCachedSuperAdminStatus(false);
    setIsInsurancePartner(false);
    setInsurancePartnerLoading(false);
    setRole(null);
    setRoleLoading(false);
    lastRoleFetchedForRef.current = null; // Reset dedup guard
    setIsAccountingUser(false);
    setAccountingUserLoading(false);
    setUser(null);
    setSession(null);
    clearMfaChallengeState();
    apiClient.setMfaFlowActive(false); // ── MFA FIX: explicit clear on sign out ──
    setStoredAccessToken(null);
    stripSupabaseRefreshTokenFromStorage((import.meta as any).env?.VITE_SUPABASE_URL);

    apiClient.clearSessionCache();
    queryClient.clear();

    try {
      localStorage.removeItem('selected_tenant_slug');
      localStorage.removeItem('afl_user_id');
      localStorage.removeItem('PW_SUPER_ADMIN_STATUS');
      localStorage.removeItem('afl_saved_views');
      localStorage.removeItem('afl_selected_view');
      localStorage.removeItem('afl_column_order');
      localStorage.removeItem('afl_column_visibility');
      localStorage.removeItem('afl_column_sizing');
      localStorage.removeItem('afl_column_pinning');
      localStorage.removeItem('afl_density');
      localStorage.removeItem('afl_compact_headers');
      localStorage.removeItem('afl_zebra_rows');
      localStorage.removeItem('afl_provider_source');
      localStorage.removeItem('PW_RECOVERY');
      localStorage.removeItem('TEST_BYPASS_AUTH');
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  };

  const value = {
    user,
    session,
    loading,
    isSuperAdmin,
    superAdminLoading,
    isInsurancePartner,
    insurancePartnerLoading,
    role,
    roleLoading,
    canAccessPortal,
    isAccountingUser,
    accountingUserLoading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithMagicLink,
    sendPasswordReset,
    updatePassword,
    isPasswordRecovery,
    mfaChallengeRequired,
    clearMfaChallengeState,
    verifyMfaChallenge,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

async function ensureMfaEnrolled(): Promise<void> {
  try {
    const requireMfa = (import.meta as any).env?.VITE_REQUIRE_MFA ?? 'true';
    debugLog('[MFA] VITE_REQUIRE_MFA:', requireMfa);

    if (String(requireMfa) !== 'true') {
      debugLog('[MFA] MFA not required, skipping enforcement');
      return;
    }

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (currentPath === '/setup-mfa' || currentPath === '/auth/set-password') {
      debugLog('[MFA] On', currentPath, ', skipping enforcement');
      return;
    }

    const { data, error } = await withTimeout(
      supabase.auth.mfa.listFactors(),
      AUTH_TIMEOUT_MS,
      'mfa.listFactors (ensureMfaEnrolled)',
    );
    debugLog('[MFA] List factors result:', { data, error });

    if (error) {
      console.error('[MFA] Error listing factors:', error);
      return;
    }

    const totpVerified = (data?.totp ?? []).some((f: any) => f.status === 'verified');
    debugLog('[MFA] Has verified TOTP:', totpVerified);

    if (!totpVerified) {
      debugLog('[MFA] Redirecting to /setup-mfa');
      apiClient.markSetupMfaInFlight();
      window.location.assign('/setup-mfa');
    }
  } catch (e) {
    if (e instanceof TimeoutError) {
      console.warn('[MFA] listFactors timed out; skipping enforcement for now');
      return;
    }
    console.error('[MFA] Unexpected error:', e);
  }
}
